import { type DecorationFontFamily, sanitizeFontFamilies } from "@innoclouds/ui";

export type LocalFontSlot = "main" | "light" | "bold";

export type SecondarySlot = Exclude<LocalFontSlot, "main">;

export interface FontSettings {
	main: string | null;
	light?: string | null;
	bold?: string | null;
	weightLight?: number | null;
	weightBold?: number | null;
	fit?: boolean | null;
	scale?: number | null;
	families?: readonly DecorationFontFamily[] | null;
}

export type FontState = { settings: FontSettings; families: DecorationFontFamily[] };

export type FontSources = { main: string; light: string | null; bold: string | null };

export const SECONDARY_SLOTS: SecondarySlot[] = ["light", "bold"];

export const LOCAL_FONT_STORAGE_KEYS: Record<LocalFontSlot, string> = {
	main: "font",
	light: "fontLight",
	bold: "fontBold",
};

export const LOCAL_FONT_WEIGHT_STORAGE_KEYS: Record<SecondarySlot, string> = {
	light: "fontWeightLight",
	bold: "fontWeightBold",
};

export const LOCAL_FONT_WEIGHTS: Record<SecondarySlot, string> = {
	light: "200",
	bold: "700",
};

export const LOCAL_FONT_SOURCE_KEY = "fontSource";

const FIT_KEY = "fontFit";
const SCALE_KEY = "fontScale";
const DECORATION_KEY = "decoration";
const FROM_DESIGN = "design";
const MIN_WEIGHT = 1;
const MAX_WEIGHT = 1000;
const MIN_SCALE = 0.5;
const MAX_SCALE = 1.5;

const readStorage = (key: string): string | null => {
	try {
		return localStorage.getItem(key)?.trim() || null;
	} catch {
		return null;
	}
};

const writeStorage = (key: string, value: string | null) => {
	try {
		if (value === null) localStorage.removeItem(key);
		else localStorage.setItem(key, value);
	} catch {}
};

const numberOf = (raw: unknown) => {
	const value = raw === null || raw === undefined || raw === "" ? Number.NaN : Number(raw);
	return Number.isFinite(value) ? value : null;
};

const textOf = (raw: unknown) => {
	const value = numberOf(raw);
	return value === null ? null : String(value);
};

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === "object" && value !== null && !Array.isArray(value);

const urlOf = (value: unknown) => (isRecord(value) && typeof value.url === "string" ? value.url : typeof value === "string" ? value : "").trim() || null;

let draft: FontSettings | null = null;

export const setDraftFonts = (settings: FontSettings | null) => {
	draft = settings;
};

const storedFonts = (): Record<string, unknown> | null => {
	const raw = readStorage(DECORATION_KEY);
	if (!raw) return null;
	try {
		const fonts: unknown = (JSON.parse(raw) as { fonts?: unknown })?.fonts;
		return isRecord(fonts) ? fonts : null;
	} catch {
		return null;
	}
};

const decorationFonts = (fonts: Record<string, unknown> | null): FontSettings | null => {
	const main = fonts && urlOf(fonts.main);
	if (!fonts || !main) return null;
	return {
		main,
		light: urlOf(fonts.light),
		bold: urlOf(fonts.bold),
		weightLight: numberOf(fonts.weight_light),
		weightBold: numberOf(fonts.weight_bold),
		fit: fonts.fit !== false,
		scale: numberOf(fonts.scale),
	};
};

const designFonts = (): FontSettings => ({
	main: readStorage(LOCAL_FONT_STORAGE_KEYS.main),
	light: readStorage(LOCAL_FONT_STORAGE_KEYS.light),
	bold: readStorage(LOCAL_FONT_STORAGE_KEYS.bold),
	weightLight: numberOf(readStorage(LOCAL_FONT_WEIGHT_STORAGE_KEYS.light)),
	weightBold: numberOf(readStorage(LOCAL_FONT_WEIGHT_STORAGE_KEYS.bold)),
	fit: readStorage(FIT_KEY) !== "0",
	scale: numberOf(readStorage(SCALE_KEY)),
});

export const readFontState = (): FontState => {
	if (draft) return { settings: draft, families: sanitizeFontFamilies(draft.families) ?? [] };
	const stored = storedFonts();
	return { settings: decorationFonts(stored) ?? designFonts(), families: sanitizeFontFamilies(stored?.families) ?? [] };
};

export const sourcesOf = (settings: FontSettings): FontSources | null => {
	const main = settings.main?.trim();
	if (!main) return null;
	return { main, light: settings.light?.trim() || null, bold: settings.bold?.trim() || null };
};

export const weightOf = (settings: FontSettings, slot: SecondarySlot): string => {
	const weight = numberOf(slot === "light" ? settings.weightLight : settings.weightBold);
	return weight !== null && weight >= MIN_WEIGHT && weight <= MAX_WEIGHT ? String(weight) : LOCAL_FONT_WEIGHTS[slot];
};

export const scaleOf = (settings: FontSettings) => {
	const scale = numberOf(settings.scale);
	return scale === null ? 1 : Math.min(MAX_SCALE, Math.max(MIN_SCALE, scale));
};

export const storeDesignFonts = (design: Record<string, unknown> | null | undefined): boolean => {
	const main = urlOf(design?.font);
	if (!main && readStorage(LOCAL_FONT_SOURCE_KEY) !== FROM_DESIGN) return false;
	const entries = Object.entries({
		[LOCAL_FONT_STORAGE_KEYS.main]: main,
		[LOCAL_FONT_STORAGE_KEYS.light]: main && urlOf(design?.font_light),
		[LOCAL_FONT_STORAGE_KEYS.bold]: main && urlOf(design?.font_bold),
		[LOCAL_FONT_WEIGHT_STORAGE_KEYS.light]: main && textOf(design?.font_weight_light),
		[LOCAL_FONT_WEIGHT_STORAGE_KEYS.bold]: main && textOf(design?.font_weight_bold),
		[FIT_KEY]: main && design?.font_fit === false ? "0" : null,
		[SCALE_KEY]: main && textOf(design?.font_scale),
		[LOCAL_FONT_SOURCE_KEY]: main ? FROM_DESIGN : null,
	});
	if (entries.every(([key, value]) => readStorage(key) === value)) return false;
	for (const [key, value] of entries) writeStorage(key, value);
	return true;
};
